import React from 'react'
import classNames from 'classnames'
import truncate from 'lodash/truncate'
import { Avatar } from 'transfix-ui/components/Avatar'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Box, makeStyles, Typography, createStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'

interface IFooterUser {
  firstName?: string
  lastName?: string
  email?: string
}

interface INavListFooterProps<T extends IFooterUser = {}> {
  user: T
  /**
   * Toggles whether to hide certain elements
   * @default true
   */
  isNavExpanded?: boolean
}

type TClassKey = 'root' | 'rootExpanded' | 'avatar' | 'infoWrapper' | 'name' | 'email'

const useNavListFooterStyles = makeStyles(theme =>
  createStyles<TClassKey, Pick<INavListFooterProps, 'isNavExpanded'>>({
    root: {
      position: 'relative',
      flex: '0 0 auto',
      display: 'flex',
      padding: theme.spacing(2),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      backgroundColor: brandColors.coolGray8,
      boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.15)',

      /**
       * Make sure that it's above the appBar level
       */
      zIndex: theme.zIndex.appBar + 1,
      /**
       * Adds a box-shadow kind of style before the footer
       */
      '&:before': {
        display: 'block',
        content: '" "',
        position: 'absolute',
        background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.05) 100%)',
        top: -12,
        height: 12,
        left: 0,
        right: 0,
      },
    },
    rootExpanded: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      overflowX: 'auto',
    },
    avatar: {
      display: 'flex',
      alignItems: 'center',
      marginRight: ({ isNavExpanded }) => theme.spacing(isNavExpanded ? 2 : 0),

      '& .avatar': {
        boxShadow: 'none',
      },
    },
    infoWrapper: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
    },
    name: {
      color: brandColors.white,
    },
    email: {
      color: brandColors.white,
    },
  })
)

const STR_MAX_LENGTH = 25

/**
 * Used as a floating/sticky footer with <NavList />
 */
export const NavListFooter = <T extends IFooterUser>({
  isNavExpanded = true,
  user,
}: INavListFooterProps<T>) => {
  const { email = '', firstName = '', lastName = '' } = user || {}
  const fullName = `${firstName} ${lastName}`.trim()

  const classes = useNavListFooterStyles({ isNavExpanded })
  return (
    <Box
      data-test='nav-list-footer'
      className={classNames(classes.root, { [classes.rootExpanded]: isNavExpanded })}>
      <Box className={classes.avatar}>
        <Avatar name={fullName} size='small' hideTooltip />
      </Box>
      {isNavExpanded && (
        <Box className={classes.infoWrapper}>
          <Box className={classes.name}>
            <Tooltip
              position='right'
              delayShow={500}
              trigger={
                <Typography variant='button' component='div'>
                  {truncate(fullName, { length: STR_MAX_LENGTH })}
                </Typography>
              }>
              {fullName}
            </Tooltip>
          </Box>
          <Box className={classes.email}>
            <Tooltip
              position='right'
              delayShow={500}
              trigger={
                <Typography variant='subtitle2' component='div' color='inherit'>
                  {truncate(email, { length: STR_MAX_LENGTH })}
                </Typography>
              }>
              {email}
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default NavListFooter
